import React from 'react';
import {
  Button,
  Box,
  Typography,
  Paper,
  styled,
  Slide,
} from '@mui/material';
import { useFormik } from 'formik';
import { servicesSchema } from '../../utils/validationSchema';
import { SERVICE_OPTIONS, ServiceType } from '../../types/partner';
import SpaIcon from '@mui/icons-material/Spa';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import SpaOutlinedIcon from '@mui/icons-material/SpaOutlined';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import BuildIcon from '@mui/icons-material/Build';
import FaceIcon from '@mui/icons-material/Face';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import FaceRetouchingNaturalIcon from '@mui/icons-material/FaceRetouchingNatural';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';

interface ServicesFormProps {
  onSubmit: (values: { services: ServiceType[] }) => void;
  onBack: () => void;
  initialValues: {
    services: ServiceType[];
  };
}

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  borderRadius: 12,
  border: '1px solid #e0e0e0',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    transform: 'translateY(-4px)',
  },
  '&.selected': {
    borderColor: theme.palette.primary.main,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    backgroundColor: theme.palette.primary.light + '20',
  }
}));

const ServicesGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: 'repeat(3, 1fr)',
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginBottom: theme.spacing(1),
  width: 48,
  height: 48,
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.light + '30',
  color: theme.palette.primary.main,
}));

const getIconForService = (service: string) => {
  switch (service) {
    case 'spa': return <SpaIcon fontSize="medium" />;
    case 'salon': return <ContentCutIcon fontSize="medium" />;
    case 'nail': return <FingerprintIcon fontSize="medium" />;
    case 'massage': return <SpaOutlinedIcon fontSize="medium" />;
    case 'cleaning': return <CleaningServicesIcon fontSize="medium" />;
    case 'repair': return <BuildIcon fontSize="medium" />;
    case 'haircut': return <ContentCutIcon fontSize="medium" />;
    case 'gym': return <FitnessCenterIcon fontSize="medium" />;
    case 'makeup': return <FaceRetouchingNaturalIcon fontSize="medium" />;
    case 'food': return <RestaurantIcon fontSize="medium" />;
    case 'other': return <MoreHorizIcon fontSize="medium" />;
    default: return <MoreHorizIcon fontSize="medium" />;
  }
};

const ServicesForm: React.FC<ServicesFormProps> = ({ onSubmit, onBack, initialValues }) => {
  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: servicesSchema,
    onSubmit: (values) => {
      onSubmit(values);
    }
  });

  const handleServiceToggle = (service: ServiceType) => {
    const currentServices = [...formik.values.services];
    const serviceIndex = currentServices.indexOf(service);
    
    if (serviceIndex === -1) {
      if (currentServices.length < 3) {
        currentServices.push(service);
      }
    } else {
      currentServices.splice(serviceIndex, 1);
    }
    
    formik.setFieldValue('services', currentServices);
  };

  const isServiceSelected = (service: ServiceType) => formik.values.services.includes(service);
  const isMaxServicesSelected = formik.values.services.length >= 3;

  return (
    <Slide direction="up" in={true} mountOnEnter unmountOnExit>
      <Box component="form" onSubmit={formik.handleSubmit} sx={{ maxWidth: 800, mx: 'auto', mt: 2 }}>
        <Typography variant="h5" component="h2" align="center" gutterBottom>
          Bạn đang kinh doanh dịch vụ nào?
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 4 }}>
          Chọn tối đa 3 dịch vụ phù hợp với doanh nghiệp của bạn.
        </Typography>

        {formik.touched.services && formik.errors.services && (
          <Typography 
            color="error" 
            align="center" 
            sx={{ mb: 2 }}
          >
            {formik.errors.services as string}
          </Typography>
        )}

        <ServicesGrid sx={{ mb: 4 }}>
          {SERVICE_OPTIONS.map((service) => (
            <StyledPaper 
              key={service.value}
              className={isServiceSelected(service.value as ServiceType) ? 'selected' : ''}
              onClick={() => handleServiceToggle(service.value as ServiceType)}
              sx={{ 
                opacity: (!isServiceSelected(service.value as ServiceType) && isMaxServicesSelected) ? 0.6 : 1,
                pointerEvents: (!isServiceSelected(service.value as ServiceType) && isMaxServicesSelected) ? 'none' : 'auto'
              }}
            >
              <IconWrapper>
                {getIconForService(service.value)}
              </IconWrapper>
              <Typography variant="body2" align="center">
                {service.label}
              </Typography>
            </StyledPaper>
          ))}
        </ServicesGrid>

        <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            color="primary"
            fullWidth
            size="large"
            onClick={onBack}
            sx={{ 
              py: 1.5, 
              borderRadius: 2,
              transition: 'all 0.3s ease',
            }}
          >
            Quay lại
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            size="large"
            disabled={formik.values.services.length === 0}
            sx={{ 
              py: 1.5, 
              borderRadius: 2,
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 16px rgba(0, 0, 0, 0.15)',
              }
            }}
          >
            Tiếp tục
          </Button>
        </Box>
      </Box>
    </Slide>
  );
};

export default ServicesForm; 